/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { IComponent, IComponentProps, msdyn365Commerce, isChannelTypeB2B } from '@msdyn365-commerce/core';
import { AsyncResult, CartLine, Customer, ProductList, ProductListLine } from '@msdyn365-commerce/retail-proxy';
import {
    addProductListLinesAsync,
    updateProductListLinesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';
import {
    FullProductInput,
    getFullProducts,
    getProductDetailsCriteriaFromActionInput,
    getProductListLines,
    GetProductListLinesInput,
    OrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, UncontrolledTooltip } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

import {
    AddToOrderTemplateDialog,
    IAddToOrderTemplateDialogResources,
    ILinesAddedToOrderTemplateDialogResources,
    LinesAddedToOrderTemplateDialog,
    IOrderTemplateNameDialogResources,
    DuplicateItemsWhenAddingToOrderTemplateDialog,
    IDuplicateItemsWhenAddingToOrderTemplateDialogResources
} from '@msdyn365-commerce/components';
export interface ISaveCartToOrderTemplateComponentProps extends IComponentProps<ISaveCartToOrderTemplateData> {
    className?: string;
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    catalogId?: number;
    addToOrderTemplateDialogResources: IAddToOrderTemplateDialogResources;
    createOrderTemplateDialogResources: IOrderTemplateNameDialogResources;
    linesAddedToOrderTemplateDialogResources: ILinesAddedToOrderTemplateDialogResources;

    createOrderTemplateFromCartButton: string;
    createOrderTemplateFromCartButtonTooltip: string;

    duplicateItemsWhenAddingToOrderTemplateDialogResources?: IDuplicateItemsWhenAddingToOrderTemplateDialogResources;
}

interface ISaveCartToOrderTemplateData {
    orderTemplates: OrderTemplate[];
    cartLines: CartLine[];
    customerInformation?: Customer;
    isChannelTypeB2B?: boolean;
}

interface ISaveCartToOrderTemplateContentState {
    showAlert?: boolean;
    orderTemplateTextMessage?: string;
    color?: string;
}

interface ISaveCartToOrderTemplateStateManager {
    waiting: boolean;
    content: ISaveCartToOrderTemplateContentState;

    setWaiting(newWaiting: boolean): void;
    setContent(newContent: ISaveCartToOrderTemplateContentState): void;
}

export interface ISaveCartToOrderTemplateComponent extends IComponent<ISaveCartToOrderTemplateComponentProps> {
    onClick(): (
        event: React.MouseEvent<HTMLElement>,
        props: ISaveCartToOrderTemplateComponentProps,
        state: ISaveCartToOrderTemplateStateManager
    ) => void;
}

const onDismiss = (state: ISaveCartToOrderTemplateStateManager): void => {
    state.setContent({ color: 'success', showAlert: false });
};

const SaveCartToOrderTemplateActions = {
    onDismiss
};

const SaveCartToOrderTemplate: React.FC<ISaveCartToOrderTemplateComponentProps> = (props: ISaveCartToOrderTemplateComponentProps) => {
    if (!isChannelTypeB2B(props.context.actionContext.requestContext)) {
        return null;
    }

    const [chosenOrderTemplate, setChosenOrderTemplate] = useState<ProductList | undefined>(undefined);
    const [addedProducts, setAddedProducts] = useState<FullProduct[] | undefined>(undefined);
    const [addToOrderTemplateModalOpen, setAddToOrderTemplateModalOpen] = useState(false);
    const [linesAddedToOrderTemplateDialogOpen, setLinesAddedToOrderTemplateDialogOpen] = useState(false);
    const [duplicateItemsWhenAddingToOrderTemplateDialogOpen, setDuplicateItemsWhenAddingToOrderTemplateDialogOpen] = useState(false);
    const [addedLinesCount, setAddedLinesCount] = useState(0);
    const [addedItemsCount, setAddedItemsCount] = useState(0);

    const {
        addToOrderTemplateDialogResources,
        createOrderTemplateDialogResources,
        createOrderTemplateFromCartButton,
        createOrderTemplateFromCartButtonTooltip
    } = props;

    const onDuplicateDialogCancel = () => {
        setDuplicateItemsWhenAddingToOrderTemplateDialogOpen(false);
        setTimeout(() => {
            setAddToOrderTemplateModalOpen(true);
        }, 500);
    };
    const isMultipleCatalogFeatureEnabledInHq = props.isMultipleCatalogFeatureEnabledInHq || false;
    const overrideQuantity = async () => {
        const newLinesToupdate: ProductListLine[] = [];

        for (const cartLine of props.data.cartLines) {
            const getLinesInput = new GetProductListLinesInput(chosenOrderTemplate?.Id!, cartLine.ProductId!.toString());
            const linesState = await getProductListLines(getLinesInput, props.context.actionContext);
            const length = 1;
            const linesToUpdate = linesState.lines.filter(line => line.productListLine.ProductId === cartLine.ProductId);
            if (linesToUpdate.length !== length) {
                // skip new line if not exist already top update
                continue;
            }
            const lineToUpdate = linesToUpdate[0].productListLine;
            lineToUpdate.UnitOfMeasure = cartLine.UnitOfMeasureSymbol || 'ea';
            lineToUpdate.Quantity = cartLine.Quantity!;
            if (isMultipleCatalogFeatureEnabledInHq) {
                lineToUpdate.CatalogId = cartLine.CatalogId;
            }
            newLinesToupdate.push(lineToUpdate);
        }

        await updateProductListLinesAsync(
            {
                callerContext: props.context.actionContext
            },
            chosenOrderTemplate!.Id,
            newLinesToupdate
        ).then(async updatedLines => {
            updateLinesCounts(updatedLines, chosenOrderTemplate);
            const allProductInputs: FullProductInput[] = getFullProductsDetails(updatedLines);
            const allProducts = await getFullProducts(allProductInputs, props.context.actionContext);
            setAddedProducts(allProducts);
            setDuplicateItemsWhenAddingToOrderTemplateDialogOpen(false);
            setTimeout(() => {
                setLinesAddedToOrderTemplateDialogOpen(true);
            }, 500);
        });
    };
    const getFullProductsDetails = (productLines: ProductListLine[]) => {
        const allProdInputs: FullProductInput[] = [];
        productLines.forEach(lines => {
            if (lines.ProductId) {
                allProdInputs.push(
                    new FullProductInput(
                        lines.ProductId,
                        props.context.actionContext.requestContext.apiSettings,
                        getProductDetailsCriteriaFromActionInput(props.context.actionContext),
                        props.context.actionContext.requestContext
                    )
                );
            }
        });
        return allProdInputs;
    };

    const onOrderTemplateSelected = async (orderTemplate: ProductList) => {
        setChosenOrderTemplate(orderTemplate);
        const allProductListLines = props.data.cartLines?.map(cartLine => {
            const productListLineToAdd: ProductListLine = {
                ProductListId: orderTemplate.Id,
                ProductId: cartLine.ProductId,
                Quantity: cartLine.Quantity,
                UnitOfMeasure: cartLine.UnitOfMeasureSymbol
            };
            return productListLineToAdd;
        });

        const allProductInputs: FullProductInput[] = getFullProductsDetails(allProductListLines);
        const allProducts = await getFullProducts(allProductInputs, props.context.actionContext);

        // Filter out products with keyed in price
        const filteredProductListLines = props.data.cartLines
            .filter(cartline => !cartline.IsPriceKeyedIn)
            .map(cartLine => {
                const productListLineToAdd: ProductListLine = {
                    ProductListId: orderTemplate.Id,
                    ProductId: cartLine.ProductId,
                    Quantity: cartLine.Quantity,
                    UnitOfMeasure: cartLine.UnitOfMeasureSymbol,
                    ...(isMultipleCatalogFeatureEnabledInHq && { catalogId: cartLine.CatalogId })
                };

                return productListLineToAdd;
            });

        await addProductListLinesAsync({ callerContext: props.context.actionContext }, orderTemplate.Id, filteredProductListLines)
            .then(filteredAddedLines => {
                updateLinesCounts(filteredAddedLines, chosenOrderTemplate);
                setAddedProducts(allProducts);
                setAddToOrderTemplateModalOpen(false);
                setTimeout(() => {
                    setLinesAddedToOrderTemplateDialogOpen(true);
                }, 500);
            })
            .catch(error => {
                if (error.name !== 'Microsoft_Dynamics_Commerce_Runtime_DuplicateObject') {
                    return AsyncResult.reject(error);
                }
                setAddToOrderTemplateModalOpen(false);
                setTimeout(() => {
                    setDuplicateItemsWhenAddingToOrderTemplateDialogOpen(true);
                }, 500);
                return AsyncResult.resolve();
            });
    };
    const updateLinesCounts = (lines: ProductListLine[], chosenOrderTemplate: ProductList | undefined) => {
        let totalLines = lines.length;
        if (chosenOrderTemplate && chosenOrderTemplate.ProductListLines !== undefined) {
            totalLines = lines.length + chosenOrderTemplate.ProductListLines?.length;
            lines.forEach(lineItem => {
                if (
                    chosenOrderTemplate.ProductListLines!.find(item => {
                        return item.ProductId == lineItem.ProductId;
                    })
                ) {
                    totalLines--;
                }
            });
        }
        setAddedLinesCount(lines.length);
        //setAddedItemsCount(lines.map(line => line.Quantity || 1).reduce((firstQuantity, secondQuantity) => firstQuantity + secondQuantity));
        setAddedItemsCount(totalLines);
    };
    const onSaveCartToOrderTemplateClick = () => {
        setAddToOrderTemplateModalOpen(true);
    };

    const areAllCartlinesPriceKeyedIn = props.data.cartLines.filter(cartline => !cartline.IsPriceKeyedIn).length === 0;

    const addToOrderTemplateRef: React.RefObject<HTMLButtonElement> = React.createRef();

    return (
        <>
            <AddToOrderTemplateDialog
                className='msc-add-to-order-template-dialog'
                modalOpen={addToOrderTemplateModalOpen}
                setModalOpen={setAddToOrderTemplateModalOpen}
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                data={{ orderTemplates: props.data.orderTemplates }}
                addToOrderTemplateDialogResources={addToOrderTemplateDialogResources}
                createOrderTemplateDialogResources={createOrderTemplateDialogResources}
                onOrderTemplateSelected={onOrderTemplateSelected}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
                {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
            />
            {chosenOrderTemplate && addedProducts && (
                <LinesAddedToOrderTemplateDialog
                    className='msc-lines-added-to-order-template-dialog'
                    id={props.id}
                    typeName={props.typeName}
                    context={props.context}
                    dialogStrings={props.linesAddedToOrderTemplateDialogResources}
                    data={{
                        linesCount: addedLinesCount,
                        itemsCount: addedItemsCount,
                        orderTemplate: chosenOrderTemplate,
                        products: addedProducts
                    }}
                    modalOpen={linesAddedToOrderTemplateDialogOpen}
                    isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
                    {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
                />
            )}
            <DuplicateItemsWhenAddingToOrderTemplateDialog
                className='msc-duplicate-items-when-adding-to-order-template-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={props.duplicateItemsWhenAddingToOrderTemplateDialogResources}
                data={{}}
                modalOpen={duplicateItemsWhenAddingToOrderTemplateDialogOpen}
                executeOverrideQuantity={overrideQuantity}
                onCancel={onDuplicateDialogCancel}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq}
                {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
            />
            <Button
                className={`msc-cart__btn-addcarttotemplate${areAllCartlinesPriceKeyedIn ? ' disabled' : ''}`}
                onClick={areAllCartlinesPriceKeyedIn ? undefined : onSaveCartToOrderTemplateClick}
                title={createOrderTemplateFromCartButton}
                innerRef={addToOrderTemplateRef}
            >
                {createOrderTemplateFromCartButton}
            </Button>
            {areAllCartlinesPriceKeyedIn && (
                <UncontrolledTooltip
                    className='msc-cart__btn-addcarttotemplate-tooltip'
                    placement='top-start'
                    trigger='hover focus'
                    target={addToOrderTemplateRef}
                >
                    {createOrderTemplateFromCartButtonTooltip}
                </UncontrolledTooltip>
            )}
        </>
    );
};

export const SaveCartToOrderTemplateComponent: React.FunctionComponent<ISaveCartToOrderTemplateComponentProps> = msdyn365Commerce.createComponentOverride<
    // @ts-expect-error
    ISaveCartToOrderTemplateComponent
>('SaveCartToOrderTemplate', { component: SaveCartToOrderTemplate, ...SaveCartToOrderTemplateActions });

export default SaveCartToOrderTemplateComponent;
